import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useLanguage } from "../../context/LanguageContext";
import { formatCurrency } from "../../utils/format";
import { motion, AnimatePresence } from "framer-motion";
import { getProductImageSrc, handleProductImageError } from "../../utils/productImage";
import "./ShoppingCart.css";

const ShoppingCart = ({ isOpen, onClose }) => {
  const { items, removeItem, updateQuantity, itemCount, totalPrice } =
    useCart();
  const { language } = useLanguage();

  // Helper to resolve bilingual fields
  const loc = (field) =>
    typeof field === 'object' && field !== null
      ? (field[language] || field.fa || field.en || '')
      : (field || '');

  const [animateExit, setAnimateExit] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [showActionMessage, setShowActionMessage] = useState(false);
  const [animatedItemId, setAnimatedItemId] = useState(null);

  // Prices are in تومان and include shipping
  const fee = Math.round(totalPrice * 0.05);
  const payable = totalPrice + fee;

  // Handle modal close with animation
  const handleClose = useCallback(() => {
    setAnimateExit(true);
    setTimeout(() => {
      setAnimateExit(false);
      onClose();
    }, 300);
  }, [onClose]);

  // Handle clicking outside to close
  const handleOutsideClick = (e) => {
    if (e.target.classList.contains("cart-overlay")) {
      handleClose();
    }
  };

  // Handle removal with animations and message
  const handleRemoveItem = (item) => {
    setAnimatedItemId(item.id);
    setActionMessage(`${loc(item.name)} از سبد خرید حذف شد`);
    setShowActionMessage(true);

    // Hide message after delay
    setTimeout(() => {
      setShowActionMessage(false);
    }, 3000);

    // Wait for animation before actual removal
    setTimeout(() => {
      removeItem(item.id);
      setAnimatedItemId(null);
    }, 300);
  };

  // Handle quantity change with message
  const handleQuantityChange = (item, newQuantity) => {
    if (newQuantity < 1) {
      handleRemoveItem(item);
      return;
    }

    if (newQuantity !== item.quantity) {
      updateQuantity(item.id, newQuantity);
      setActionMessage(`تعداد ${loc(item.name)} به ${newQuantity} تغییر کرد`);
      setShowActionMessage(true);

      // Hide message after delay
      setTimeout(() => {
        setShowActionMessage(false);
      }, 3000);
    }
  };

  // Apply escape key to close cart
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [handleClose, isOpen]);

  // Hook a global event to open the cart from floating button
  useEffect(() => {
    const open = () => {
      // If parent controls isOpen, just call onClose? We can't open here, so dispatch to parent
      // As a fallback, simulate click on header cart if exists
      if (!isOpen) {
        try { document.querySelector('.cart-icon')?.click(); } catch(_) {}
      }
    };
    window.addEventListener("openCart", open);
    return () => window.removeEventListener("openCart", open);
  }, [handleClose, isOpen]);

  // Apply body scroll lock when cart is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="cart-overlay" onClick={handleOutsideClick}>
      <AnimatePresence>
        {showActionMessage && (
          <motion.div
            className="cart-action-message"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {actionMessage}
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className={`shopping-cart ${animateExit ? "slide-out" : "slide-in"}`}
      >
        <div className="cart-header">
          <h2>
            سبد خرید{" "}
            <span className="cart-count">
              {itemCount > 0 && `(${itemCount})`}
            </span>
          </h2>
          <button
            className="close-button"
            onClick={handleClose}
            aria-label="بستن سبد خرید"
          >
            ×
          </button>
        </div>

        {itemCount === 0 ? (
          <div className="empty-cart">
            <div className="empty-cart-icon">
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <path d="M16 10a4 4 0 0 1-8 0"></path>
              </svg>
            </div>
            <p>سبد خرید شما خالی است</p>
            <Link
              to="/shop"
              onClick={handleClose}
              className="continue-shopping"
            >
              برو به فروشگاه
            </Link>
          </div>
        ) : (
          <>
            <div className="cart-items">
              {items.map((item) => (
                <motion.div
                  key={item.id}
                  className={`cart-item ${
                    animatedItemId === item.id ? "removing" : ""
                  }`}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, height: 0, marginTop: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="item-image">
                    <img
                      src={getProductImageSrc(item.image)}
                      alt={loc(item.name)}
                      onError={handleProductImageError}
                    />
                  </div>
                  <div className="item-details">
                    <h3>{loc(item.name)}</h3>
                    <div className="item-options">
                      {item.size && (
                        <span className="item-size">اندازه: {item.size}</span>
                      )}
                      {item.color && (
                        <span className="item-color">
                          رنگ: {item.color}
                          <span
                            className="color-dot"
                            style={{
                              backgroundColor: item.colorCode || item.color,
                            }}
                          />
                        </span>
                      )}
                    </div>
                    <div className="item-price">
                      <span>
                        {formatCurrency(item.salePrice || item.price, language)}
                      </span>
                    </div>
                  </div>
                  <div className="item-actions">
                    <div className="quantity-controls">
                      <button
                        onClick={() =>
                          handleQuantityChange(item, item.quantity - 1)
                        }
                        aria-label="کاهش تعداد"
                      >
                        -
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        onClick={() =>
                          handleQuantityChange(item, item.quantity + 1)
                        }
                        aria-label="افزایش تعداد"
                      >
                        +
                      </button>
                    </div>
                    <button
                      className="remove-item"
                      onClick={() => handleRemoveItem(item)}
                      aria-label={`حذف ${loc(item.name)} از سبد خرید`}
                    >
                      حذف
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>

              <div className="cart-summary">
                <div className="summary-row">
                  <span>مبلغ کالاها:</span>
                  <span>{formatCurrency(totalPrice, language)}</span>
                </div>
                <div className="summary-row">
                  <span>کارمزد (۵٪):</span>
                  <span>{formatCurrency(fee, language)}</span>
                </div>
                <div className="summary-divider"></div>
                <div className="summary-total">
                  <span>مبلغ قابل پرداخت:</span>
                  <span>{formatCurrency(payable, language)}</span>
                </div>
              </div>

            <div className="cart-actions">
              <Link
                to="/checkout"
                onClick={handleClose}
                className="checkout-button"
              >
                نهایی کردن خرید
              </Link>
              <button onClick={handleClose} className="continue-button">
                ادامه خرید
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShoppingCart;
